import { useState } from 'react';
import { useAlerts, useAlertCounts, useDismissAlert, useMarkAllAlertsRead, useGenerateAlerts } from '@/hooks/useAlerts';
import { AlertType, Alert } from '@/types';
import Button from '@/components/ui/Button';
import { TrendingDown, Calendar, Check, RefreshCw, Bell, Percent } from 'lucide-react';
import clsx from 'clsx';

export default function Alerts() {
  const [filterType, setFilterType] = useState<AlertType | ''>('');
  const { data: alerts, isLoading, refetch } = useAlerts({
    alert_type: filterType || undefined,
  });
  const { data: counts } = useAlertCounts();
  const dismissAlert = useDismissAlert();
  const markAllRead = useMarkAllAlertsRead();
  const generateAlerts = useGenerateAlerts();

  const alertTypeConfig: Record<AlertType, { icon: any; label: string; color: string; borderColor: string }> = {
    low_stock: {
      icon: TrendingDown,
      label: 'Stock Bajo',
      color: 'text-yellow-600 bg-yellow-100',
      borderColor: 'border-yellow-300',
    },
    expiration: {
      icon: Calendar,
      label: 'Por Vencer',
      color: 'text-orange-600 bg-orange-100',
      borderColor: 'border-orange-300',
    },
    low_margin: {
      icon: Percent,
      label: 'Margen Bajo',
      color: 'text-red-600 bg-red-100',
      borderColor: 'border-red-300',
    },
  };

  const filterOptions = [
    { value: '', label: 'Todas', count: counts?.total || 0 },
    { value: 'low_stock', label: 'Stock Bajo', count: counts?.low_stock || 0 },
    { value: 'expiration', label: 'Por Vencer', count: counts?.expiration || 0 },
    { value: 'low_margin', label: 'Margen Bajo', count: counts?.low_margin || 0 },
  ];

  const handleDismiss = async (id: number) => {
    try {
      await dismissAlert.mutateAsync(id);
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead.mutateAsync(filterType || undefined);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleGenerateAlerts = async () => {
    try {
      const result = await generateAlerts.mutateAsync();
      alert(`Se generaron ${result.generated.total} nuevas alertas`);
      refetch();
    } catch (error) {
      console.error('Error generating alerts:', error);
    }
  };

  // Tarjeta de una alerta. `sinStock` la resalta en rojo (producto en 0).
  const renderAlert = (a: Alert, sinStock = false) => {
    const config = alertTypeConfig[a.alert_type];
    const Icon = config.icon;
    const color = sinStock ? 'text-red-600 bg-red-100' : config.color;
    const borderColor = sinStock ? 'border-red-400' : config.borderColor;
    return (
      <div
        key={a.id}
        className={clsx('card flex items-start gap-4 border-l-4', borderColor, !a.is_read && 'ring-1 ring-primary-200')}
      >
        <div className={clsx('p-3 rounded-lg', color)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900">{a.title}</h3>
            {!a.is_read && <span className="w-2 h-2 bg-primary-500 rounded-full" />}
          </div>
          {a.message && <p className="text-sm text-gray-600 mt-1">{a.message}</p>}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className={clsx('px-2 py-0.5 rounded', color)}>{config.label}</span>
            <span>
              {new Date(a.created_at).toLocaleDateString('es-AR', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => handleDismiss(a.id)} loading={dismissAlert.isPending}>
          Descartar
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alertas</h1>
          <p className="text-gray-600">
            {counts?.unread || 0} alertas sin leer
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleGenerateAlerts}
            loading={generateAlerts.isPending}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Verificar Alertas
          </Button>
          {(counts?.unread || 0) > 0 && (
            <Button
              variant="secondary"
              onClick={handleMarkAllRead}
              loading={markAllRead.isPending}
            >
              <Check className="h-4 w-4 mr-2" />
              Marcar todas leídas
            </Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        {filterOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilterType(option.value as AlertType | '')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              filterType === option.value
                ? 'bg-primary-100 text-primary-700'
                : 'bg-white text-gray-600 hover:bg-gray-100 border'
            )}
          >
            {option.label}
            {option.count > 0 && (
              <span className="ml-2 bg-gray-200 px-2 py-0.5 rounded-full text-xs">
                {option.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Lista de alertas (stock sin stock / agotándose separados) */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : alerts && alerts.length > 0 ? (
          (() => {
            const sinStock = alerts.filter((a) => a.alert_type === 'low_stock' && a.priority === 1);
            const agotandose = alerts.filter((a) => a.alert_type === 'low_stock' && a.priority !== 1);
            const otras = alerts.filter((a) => a.alert_type !== 'low_stock');
            return (
              <>
                {sinStock.length > 0 && (
                  <>
                    <h2 className="flex items-center gap-2 text-sm font-semibold text-red-700 mt-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      Sin stock ({sinStock.length})
                    </h2>
                    {sinStock.map((a) => renderAlert(a, true))}
                  </>
                )}
                {agotandose.length > 0 && (
                  <>
                    <h2 className="flex items-center gap-2 text-sm font-semibold text-yellow-700 mt-3">
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                      Agotándose ({agotandose.length})
                    </h2>
                    {agotandose.map((a) => renderAlert(a))}
                  </>
                )}
                {otras.length > 0 && (
                  <>
                    {(sinStock.length > 0 || agotandose.length > 0) && (
                      <h2 className="text-sm font-semibold text-gray-600 mt-3">Otras alertas ({otras.length})</h2>
                    )}
                    {otras.map((a) => renderAlert(a))}
                  </>
                )}
              </>
            );
          })()
        ) : (
          <div className="card text-center py-12">
            <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No hay alertas activas</p>
          </div>
        )}
      </div>
    </div>
  );
}
